/**
 * My Queries Page
 * View all flip requests for logged-in homeowner
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Home,
  DollarSign,
  Users,
  Loader2,
  Eye,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../contexts/AuthContext';
import API_CONSTRUCTOR from '../../models/api-model';

// Status configuration with colors and icons
const STATUS_CONFIG = {
  pending_inspection_payment: {
    label: 'Awaiting Inspection Payment',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: DollarSign
  },
  pending_agent: {
    label: 'In Review',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: Clock
  },
  pending_owner_approval: {
    label: 'Ready for Review',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: AlertCircle
  },
  approved_by_owner: {
    label: 'Open to Investors',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: Users
  },
  investor_committed: {
    label: 'Investors Committed',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    icon: Users
  },
  completed: {
    label: 'Completed',
    color: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-300',
    icon: CheckCircle
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: XCircle
  },
  rejected_by_owner: {
    label: 'Revision Requested',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: XCircle
  }
};

const QueryCard = ({ query, onClick }) => {
  const statusConfig = STATUS_CONFIG[query.status] || STATUS_CONFIG.pending_agent;
  const Icon = statusConfig.icon;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
            <div className="flex items-center gap-2 mb-2">
              <Home className="h-5 w-5 text-primary flex-shrink-0" />
              <h3 className="text-lg font-semibold truncate">{query.property_address}</h3>
            </div>
            {query.property_city && (
              <p className="text-sm text-muted-foreground">
                {query.property_city}, {query.property_state} {query.property_zip}
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/property/${query.property_id}`, '_blank');
              }}
              title="View Property"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onClick} title="View Query Details">
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Status Badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border mb-4`}>
          <Icon className="h-4 w-4" />
          {statusConfig.label}
        </div>

        {/* Query Info */}
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Submitted</p>
            <p className="text-sm font-medium">{formatDate(query.created_at)}</p>
          </div>
          {query.total_investment_base && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Investment Base</p>
              <p className="text-sm font-medium">{formatCurrency(query.total_investment_base)}</p>
            </div>
          )}
        </div>

        {/* Inspection Payment Notice */}
        {query.status === 'pending_inspection_payment' && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              <strong>Action Required:</strong> Inspection payment of ${query.inspection_fee_amount} needed to proceed.
            </p>
          </div>
        )}

        {/* Calculation Ready Notice */}
        {query.status === 'pending_owner_approval' && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-800">
              <strong>Action Required:</strong> Your calculation is ready for review!
            </p>
          </div>
        )}

        {/* Investor Interest */}
        {(query.status === 'approved_by_owner' || query.status === 'investor_committed') && query.number_of_investors > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>{query.number_of_investors}</strong> investor{query.number_of_investors > 1 ? 's' : ''} interested
              {query.total_committed_percentage && ` • ${query.total_committed_percentage}% funded`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MyQueries = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiModel = API_CONSTRUCTOR();

  const [queries, setQueries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, active, completed

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiModel.get('/queries/my-queries');

      if (response.success) {
        setQueries(response.data);
      } else {
        throw new Error(response.error || 'Failed to load queries');
      }
    } catch (err) {
      console.error('Fetch queries error:', err);
      setError(err.message || 'Failed to load your flip requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryClick = (queryId) => {
    navigate(`/queries/${queryId}`);
  };

  const filterQueries = (queries) => {
    if (filter === 'all') return queries;
    
    if (filter === 'pending') {
      return queries.filter(q => 
        ['pending_inspection_payment', 'pending_agent', 'pending_owner_approval'].includes(q.status)
      );
    }
    
    if (filter === 'active') {
      return queries.filter(q => 
        ['approved_by_owner', 'investor_committed'].includes(q.status)
      );
    }
    
    if (filter === 'completed') {
      return queries.filter(q => 
        ['completed', 'cancelled', 'rejected_by_owner'].includes(q.status)
      );
    }
    
    return queries;
  };

  const filteredQueries = filterQueries(queries);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">My Flip Requests</h1>
        <p className="text-muted-foreground">
          Track and manage your property flipping projects
        </p>
      </div>

      {/* Stats Overview */}
      {!isLoading && queries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Requests</p>
              <p className="text-2xl font-bold">{queries.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Pending Review</p>
              <p className="text-2xl font-bold">
                {queries.filter(q => ['pending_inspection_payment', 'pending_agent', 'pending_owner_approval'].includes(q.status)).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Active</p>
              <p className="text-2xl font-bold">
                {queries.filter(q => ['approved_by_owner', 'investor_committed'].includes(q.status)).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Completed</p>
              <p className="text-2xl font-bold">
                {queries.filter(q => q.status === 'completed').length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({queries.length})
        </Button>
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
        >
          Pending ({queries.filter(q => ['pending_inspection_payment', 'pending_agent', 'pending_owner_approval'].includes(q.status)).length})
        </Button>
        <Button
          variant={filter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('active')}
        >
          Active ({queries.filter(q => ['approved_by_owner', 'investor_committed'].includes(q.status)).length})
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
        >
          Completed ({queries.filter(q => ['completed', 'cancelled', 'rejected_by_owner'].includes(q.status)).length})
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <Skeleton className="h-8 w-32 mb-4" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
            <Button
              onClick={fetchQueries}
              className="mt-4"
              variant="outline"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredQueries.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">
              {filter === 'all' ? 'No flip requests yet' : `No ${filter} requests`}
            </h3>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? 'Start your first property flip by searching for a property'
                : `You don't have any ${filter} requests at the moment`}
            </p>
            {filter === 'all' ? (
              <Button onClick={() => navigate('/search')}>
                Search Properties
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setFilter('all')}>
                View All Requests
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Queries List */}
      {!isLoading && !error && filteredQueries.length > 0 && (
        <div className="space-y-4">
          {filteredQueries.map(query => (
            <QueryCard
              key={query.id}
              query={query}
              onClick={() => handleQueryClick(query.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyQueries;


